import { useContext, useMemo, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { GlobalStates } from '../GlobalStates';
import useGlobalData from './useGlobalData';
import { useQuery } from '@tanstack/react-query';

/**
 * Custom hook for efficiently loading company-specific data
 * Ensures that non-admin users only receive their company's data
 */



export function useCompanyData({ isAdmin, companyName, kasPildo, year }) {
  const { user } = useContext(GlobalStates);
  const { setOriginalData } = useGlobalData();
  


    const { data: answers, isFetching: isLoadingAnswers } = useQuery({
      queryKey: [
        "answers",
        year,
        kasPildo,
        companyName,
      ],
      queryFn: async () => {
      
        // For non-admins, always enforce their company filter
        const userCompany = user?.user_metadata?.imones_pavadinimas || user?.imonesPavadinimas;
        if (!isAdmin && (!companyName || companyName !== userCompany)) {
          console.warn('Company mismatch or missing company data:', { companyName, userCompany });
          throw new Error('Non-admin users can only access their own company data');
        }
        
        // Prepare filter conditions
        let query = supabase.from('atsakymai').select('*');
        
        // Always apply company filter for non-admins
        if (companyName) {
          query = query.eq('imones_pavadinimas', companyName);
        }
        
        // Apply additional filters if provided
        if (kasPildo) {
          query = query.eq('kas_pildo', kasPildo);
        }
        
        // Apply year filter if provided
        if (year) {
          // Assuming uzpildymo_data is in YYYY-MM-DD format
          const yearStart = `${year}-01-01`;
          const yearEnd = `${year}-12-31`;
          query = query.gte('uzpildymo_data', yearStart).lte('uzpildymo_data', yearEnd);
        }
        
        // Execute the query
        const { data, error: queryError } = await query;
        
        if (queryError) throw queryError;
        
        // Format the data for the app's expected structure
        return data.map(item => ({
          id: item.id,
          attributes: {
            imonesPavadinimas: item.imones_pavadinimas,
            kasPildo: item.kas_pildo,
            atsakymai: item.atsakymai,
            uzpildymoData: item.uzpildymo_data,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          }
        }));
  
      },
      enabled: !!kasPildo,
      staleTime: 3 * 60 * 1000, // 3 minutes
      cacheTime: 8 * 60 * 1000, // 8 minutes
    });

    const { data: tipai, isLoading: isTipaiLoading } = useQuery({
      queryKey: [
        "forms-tendencijos",
        kasPildo
      ],
      queryFn: async () => {
        const { data: tipaiData, error: tipaiError } = await supabase
        .from('formos')
        .select('*')
        .eq('kas_pildo', kasPildo);
      
      if (tipaiError) throw tipaiError;
      
      const formattedTipai = tipaiData.map(item => ({
        id: item.id,
        attributes: {
          kasPildo: item.kas_pildo,
          formObject: item.form_object,
          formosPavadinimas: item.formos_pavadinimas,
          imonesPavadinimas: item.imones_pavadinimas,
        }
      }));

      return formattedTipai;
      },
      enabled: !!kasPildo,
      staleTime: 8 * 60 * 1000, // 8 minutes - forms change less frequently
      cacheTime: 12 * 60 * 1000, // 12 minutes
    });

  useEffect(() => {
    if (isTipaiLoading) return;

    setOriginalData({
      tipai: tipai,
    });
  }, [tipai, setOriginalData, isTipaiLoading]);
 
  
  useEffect(() => {
    if (isLoadingAnswers) return;

    setOriginalData({
      atsakymai: answers,
    });
  }, [answers, setOriginalData, isLoadingAnswers]);


  
  return {
    isLoading: isLoadingAnswers || isTipaiLoading,
  };
}

export default useCompanyData;
